/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useState } from "react";
import { IInspectorControl_Icon, TInspectorControlProps } from "../InspectorControlTypes";
import { FieldGroup, FieldIconSelector } from "@adapptio/lib-editor-ui";
import { useEditorMainContext } from "../../../ui/contexts/EditorMainContext";
import { useEditorInspectorContext } from "../../../ui/contexts/EditorInspectorContext";
import { useSimpleEvent } from "../../../ui/hooks/useSimpleEvent";
import { useBlueprintModel } from "../../../ui/hooks/useBlueprintModel";
import { isModelValueRequired } from "@adapptio/lib-blueprint";

export interface IInspectorControlIconProps extends TInspectorControlProps<IInspectorControl_Icon> {}

export const InspectorControlIcon: React.FC<IInspectorControlIconProps> = ({ model: _model }) => {
	const editorContext = useEditorMainContext();
	const inspectorContext = useEditorInspectorContext();

	const { model, validationErrors } = useBlueprintModel(_model, "constString");

	const required = isModelValueRequired(model);

	const [ iconPacks, setIconPacks ] = useState(editorContext.resourceService.getIconPacks());
	const [ urlMapping, setUrlMapping ] = useState(editorContext.resourceService.getIconPackUrlMapping());

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	useSimpleEvent(
		editorContext.resourceService.onIconPacksChanged,
		() => {
			setIconPacks(editorContext.resourceService.getIconPacks());
			setUrlMapping(editorContext.resourceService.getIconPackUrlMapping());
		},
		[ editorContext.resourceService ]
	);

	return (
		<FieldGroup grow>
			<FieldIconSelector
				value={model.value}
				iconPacks={iconPacks}
				urlMapping={urlMapping}
				onChange={handleChange}
				validationErrors={validationErrors}
				readOnly={inspectorContext?.readOnly}
				// @todo Translate
				placeholder="Select icon"
				clearable={!required}
			/>
		</FieldGroup>
	);
};
